import { useState, useEffect, useMemo } from 'react'
import firebase from '../firebase/compat'
import EffectDatabase from '../components/meetstandaard/EffectDatabase'
import {
    effectRow,
    ladderRow,
    workSkillsRow,
    materialRow,
    MATERIAL_OUTPUTS,
} from '../helpers/dashboard/buildBenchmarkRows'
import { EFFECTEN as ARBEIDSVAARDIGHEDEN_EFFECTEN } from '../helpers/arbeidsvaardighedenHelpers/arbeidsvaardighedenStandaard'
import { client } from './Client'

// Per effect één callable. In blokken, want de meetstandaard telt tientallen
// effecten en de browser knijpt gelijktijdige verzoeken toch af.
const REQUEST_BATCH_SIZE = 5

/**
 * De benchmarksterkte per MSI-effect, met per rij wat er nog nodig is om een
 * trede te stijgen (`nextLevelGaps`) en of de klantdrempel gehaald is
 * (`strengthScore` ≥ `MIN_CLIENT_STRENGTH_SCORE`).
 *
 * Gedeeld door de monitorkaart op het dashboard en de Intelligence-pagina, zodat
 * die twee niet elk hun eigen fan-out en rijopbouw krijgen — dezelfde vraag hoort
 * één antwoord te hebben.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * `loaded` is een eigen toestand: "geen benchmarks" is een bewering aan de lezer
 * en die mag pas gedaan worden als de reads gelukt zijn.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * Naast de MSI-effecten komen de drie outputs mee: participatieladder,
 * arbeidsvaardigheden en materialen. Valt één van die bronnen uit, dan blijven
 * de andere staan en komt de naam in `failedSources` — stil een rij weglaten
 * leest als 'die output bestaat niet', en dat is iets anders dan 'kon niet
 * geladen worden'.
 *
 * @returns {{rows: Array<object>, loading: boolean, loaded: boolean, failedSources: Array<string>, outputsLoaded: boolean, error: (Error|null)}}
 */
const useBenchmarkRows = () => {
    const effectDatabase = EffectDatabase()
    const [rows, setRows] = useState(/** @type {Array<object>} */([]))
    const [outputRows, setOutputRows] = useState(/** @type {Array<object>} */([]))
    const [failedSources, setFailedSources] = useState(/** @type {Array<string>} */([]))
    // Eigen laadstand voor de outputs: ze komen los van de effect-fan-out
    // binnen, en `loaded` gaat alleen over die fan-out.
    const [outputsLoaded, setOutputsLoaded] = useState(false)
    const [loaded, setLoaded] = useState(false)
    const [error, setError] = useState(/** @type {Error|null} */(null))

    // De drie outputbronnen: elk één aanroep, los van de effect-fan-out. Een
    // uitval hier is niet fataal voor de rest.
    useEffect(() => {
        let cancelled = false
        const fn = (naam) => firebase.app().functions('europe-west1').httpsCallable(naam)

        const bronnen = [
            {
                naam: 'participatieladder',
                haal: () => fn('participationLadderBenchmark')({ callerCompanyId: client })
                    .then((r) => [ladderRow(r.data || {})]),
            },
            {
                naam: 'arbeidsvaardigheden',
                haal: () => fn('workSkillsBenchmark')({
                    callerCompanyId: client,
                    effectIds: ARBEIDSVAARDIGHEDEN_EFFECTEN.map((e) => e.id),
                }).then((r) => {
                    const results = (r.data && r.data.results) || {}
                    return ARBEIDSVAARDIGHEDEN_EFFECTEN
                        .filter((effect) => results[effect.id])
                        .map((effect) => workSkillsRow({
                            effectId: effect.id,
                            effectName: effect.naam,
                            benchmark: results[effect.id],
                        }))
                }),
            },
            {
                naam: 'materialen',
                haal: () => Promise.all(MATERIAL_OUTPUTS.map((o) => fn('materialOutputBenchmark')({
                    callerCompanyId: client,
                    output: o.output,
                }).then((r) => materialRow({ ...o, benchmark: r.data || {} })))),
            },
        ]

        Promise.all(bronnen.map((bron) => bron.haal().catch((err) => {
            console.error(`useBenchmarkRows ${bron.naam}:`, err)
            return { mislukt: bron.naam }
        }))).then((uitkomsten) => {
            if (cancelled) return
            setOutputRows(uitkomsten.filter(Array.isArray).flat())
            setFailedSources(uitkomsten.filter((u) => u && u.mislukt).map((u) => u.mislukt))
            setOutputsLoaded(true)
        })

        return () => { cancelled = true }
    }, [])

    // De catalogus is een nieuw array per render; sleutelen op de inhoud
    // voorkomt dat de fan-out elke render opnieuw begint.
    const effectDatabaseKey = useMemo(() => {
        if (!Array.isArray(effectDatabase)) return ''
        return effectDatabase
            .flatMap((category) => (category.effects || []).map((effect) => effect.id))
            .join(',')
    }, [effectDatabase])

    useEffect(() => {
        if (!Array.isArray(effectDatabase) || effectDatabase.length === 0) return undefined

        let cancelled = false
        setLoaded(false)
        setError(null)
        setRows([])

        const effects = effectDatabase.flatMap((category) => (category.effects || []).map((effect) => ({
            category: category.name,
            effectId: effect.id,
            effectName: effect.name,
            // Sectoren horen bij het effect, niet bij de categorie — de
            // catalogus levert ze per effect.
            sectors: Array.isArray(effect.sectors) ? effect.sectors : [],
        })))

        const msiBenchmark = firebase.app().functions('europe-west1').httpsCallable('msiBenchmark')

        const laden = async () => {
            const verzameld = []
            for (let i = 0; i < effects.length; i += REQUEST_BATCH_SIZE) {
                const blok = effects.slice(i, i + REQUEST_BATCH_SIZE)
                // eslint-disable-next-line no-await-in-loop
                const uitkomst = await Promise.all(blok.map(async (effect) => {
                    const result = await msiBenchmark({ msiId: effect.effectId, callerCompanyId: client })
                    return effectRow({ ...effect, benchmark: result.data || {} })
                }))
                verzameld.push(...uitkomst)
                // Tussentijds tonen: bij tientallen effecten duurt de hele
                // fan-out te lang om niets te laten zien.
                if (!cancelled) setRows([...verzameld])
            }
            if (!cancelled) setLoaded(true)
        }

        laden().catch((err) => {
            // Laten zien, niet wegslikken: een geweigerde read mag nooit als
            // 'geen benchmarks' op het scherm belanden.
            console.error('useBenchmarkRows:', err)
            if (cancelled) return
            setError(err)
            setRows([])
            setLoaded(false)
        })

        return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectDatabaseKey])

    // Eén array per inhoudswijziging, niet per render: afnemers memoïzeren erop.
    const alleRijen = useMemo(() => [...rows, ...outputRows], [rows, outputRows])

    return {
        rows: alleRijen,
        loading: !loaded && error === null,
        loaded,
        failedSources,
        outputsLoaded,
        error,
    }
}

export default useBenchmarkRows
